/**
 * The decision on an applicant: approve or reject an organization
 * registration, approve or reject a federated partner.
 *
 * Both halves of the queue are `require_admin` and single-approver. The rows
 * live on the Overview board, which is why these are components and not a
 * page: the board draws them inline on each applicant's row. A viewer sees
 * the same two buttons disabled, so the row still says what a decision
 * would look like.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import { approveRegistration, rejectRegistration } from '@optum/api-client/endpoints/registrations';
import type { FederatedPartner, Registration } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Dialog, DialogActions } from '../../ui/Dialog.js';
import { Field } from '../../ui/Field.js';
import { Input } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import type { usePartnerReview } from './partner-review.js';

export type PartnerReview = ReturnType<typeof usePartnerReview>;

/** Approve runs at once; Reject asks for the reason the record will carry. */
export function RegistrationActions({ reg, canMutate }: { reg: Registration; canMutate: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');

  const approve = useMutation({
    mutationFn: () => approveRegistration(reg.id),
    onSuccess: () => {
      toast.success(`Approved ${reg.name}`, 'Onboarding started.');
      qc.invalidateQueries({ queryKey: qk.registrations() });
      qc.invalidateQueries({ queryKey: qk.orgs() });
      // The registration id is the organization id (`/registrations/{orgId}/
      // approve`), so approval can hand straight over to the tenant's home,
      // where the onboarding card is. Approving and then leaving the operator
      // on the board is how a tenant ends up approved but never set up.
      navigate(`/orgs/${reg.id}/overview`);
    },
    onError: (err) =>
      toast.error('Approve failed', err instanceof ApiError ? err.message : String(err)),
  });

  const reject = useMutation({
    mutationFn: (r: string) => rejectRegistration(reg.id, r),
    onSuccess: () => {
      toast.success(`Rejected ${reg.name}`);
      qc.invalidateQueries({ queryKey: qk.registrations() });
      setRejectOpen(false);
      setReason('');
    },
    onError: (err) =>
      toast.error('Reject failed', err instanceof ApiError ? err.message : String(err)),
  });

  if (reg.status !== 'pending') return null;

  return (
    <>
      <div style={{ display: 'inline-flex', gap: 7 }}>
        <Button
          size="sm"
          variant="danger"
          onClick={() => setRejectOpen(true)}
          disabled={!canMutate || approve.isPending}
        >
          Reject
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => approve.mutate()}
          disabled={!canMutate || approve.isPending}
        >
          {approve.isPending ? 'Approving…' : 'Approve'}
        </Button>
      </div>
      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={<>Reject {reg.name}?</>}
        subtitle="Provide a short reason — stored for audit and shown on the registration record."
      >
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            if (reason.trim()) reject.mutate(reason.trim());
          }}
          style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 11 }}
        >
          <Field id="reject-reason" label="Reason" required>
            <Input
              id="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
              required
            />
          </Field>
          <DialogActions>
            <Button type="button" variant="ghost" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" disabled={reject.isPending}>
              {reject.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

/**
 * The partner half. The mutations and the two dialogs (reason, one-time
 * token) belong to `usePartnerReview`, which the host page owns and renders
 * once; this row only triggers them.
 */
export function PartnerActions({
  partner,
  review,
  canMutate,
}: {
  partner: FederatedPartner;
  review: PartnerReview;
  canMutate: boolean;
}) {
  if (partner.status !== 'pending') return null;
  return (
    <div style={{ display: 'inline-flex', gap: 7 }}>
      <Button
        size="sm"
        variant="danger"
        onClick={() => review.startReject(partner.id)}
        disabled={!canMutate || review.isApproving}
      >
        Reject
      </Button>
      <Button
        size="sm"
        variant="primary"
        onClick={() => review.approve(partner.id)}
        disabled={!canMutate || review.isApproving}
      >
        Approve
      </Button>
    </div>
  );
}
